import { DrawerLink } from '../../app/DrawerLink';
import { Link, useLocation } from 'react-router-dom';

import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { BufferRing } from '../../components/ui/Loader';
import { usePlayer } from '../../stores/player';
import { stopListening } from '../rooms/listening';

/**
 * The dock, while you are following a broadcast (§6.8).
 *
 * ## Why the dock cannot just be the dock here
 *
 * Every transport control it normally shows is a lie during a broadcast.
 * Scrubbing is undone by `useRoomSync` within a second - everybody in a room is
 * at the same instant, which is what a room *is*. "Next" loads whatever happens
 * to be in the local queue and drops you out of the broadcast without saying
 * so. Even pause is strange: the show carries on without you and you rejoin
 * wherever it has got to, which is not what a pause button means anywhere else.
 *
 * So the controls are not disabled, they are absent, and what replaces them is
 * the two things that are actually true: this is live, and you can leave.
 *
 * ## Getting back
 *
 * A broadcast has no URL of its own - it lives at its host's profile - which is
 * easy to lose once you have navigated three pages away from it. The host's
 * name is the way back, and it stops being a link only when you are already
 * there, where a link to the page you are on is furniture.
 */
export function BroadcastBar({
  username,
  host,
  name,
}: {
  username: string;
  host: string;
  /** What tonight's set is called. Most broadcasts are not called anything. */
  name: string | null;
}) {
  const track = usePlayer((s) => s.track);
  const loading = usePlayer((s) => s.loading);
  const error = usePlayer((s) => s.error);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const setVolume = usePlayer((s) => s.setVolume);
  const toggleMute = usePlayer((s) => s.toggleMute);

  const { pathname } = useLocation();
  const here = pathname === `/${username}`;

  return (
    <div
      role="region"
      aria-label={`Listening to ${host} live`}
      data-player-dock
      data-broadcast-dock
      className="flex h-full items-center gap-4 px-6"
    >
      <span className="flex shrink-0 items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-3 py-1.5">
        {/* The one animated thing in the bar, and only while actually live. */}
        <span className="h-2 w-2 animate-pulse rounded-full bg-hot" aria-hidden />
        <span className="meta-caps text-hot-text">Live</span>
      </span>

      {track ? (
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          {(track.artwork_small ?? track.artwork) ? (
            <img
              src={track.artwork_small ?? track.artwork ?? undefined}
              alt=""
              className="h-11 w-11 rounded-[var(--radius-control)] object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg, #151530 0 6px, #101024 6px 12px)',
              }}
            />
          )}

          <div className="min-w-0 max-w-[240px]">
            <DrawerLink
              to={`/tracks/${track.id}`}
              className="block truncate text-sm font-medium hover:underline"
            >
              {track.title}
            </DrawerLink>
            <div className="meta truncate text-faint">{track.owner?.display_name ?? ''}</div>
          </div>
        </div>
      ) : (
        // A real state, not a gap: the host is between tracks and the room is
        // still theirs. Going blank here would read as the broadcast ending.
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          <div className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] bg-raised" aria-hidden />
          <div className="min-w-0">
            <div className="text-sm text-faint">Between tracks</div>
            <div className="meta text-faint">still on air</div>
          </div>
        </div>
      )}

      {/*
        Whose show it is, which the normal dock has no room for and which
        matters more here than the track does: you can be hearing a stranger's
        record with no idea where the sound is coming from.
      */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="meta shrink-0 text-faint">on</span>

        {here ? (
          <span className="min-w-0 truncate text-sm font-medium text-ink">{host}</span>
        ) : (
          <Link
            to={`/${username}`}
            data-back-to-room
            className="min-w-0 truncate rounded-[var(--radius-pill)] border border-line px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
          >
            {host}
            <span className="meta ml-2 text-faint">back to the room →</span>
          </Link>
        )}

        {name && <span className="meta hidden truncate text-ghost xl:block">{name}</span>}

        {loading && <BufferRing size={18} />}
        {error && <span className="meta shrink-0 text-hot-text">{error}</span>}
      </div>

      {track && (
        <FavoriteButton
          trackId={track.id}
          favorited={track.favorited}
          likeCount={track.like_count ?? 0}
          size="sm"
          className="shrink-0"
        />
      )}

      <div className="hidden shrink-0 items-center gap-2 lg:flex">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
          className="text-soft hover:text-ink"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
            {muted || volume === 0 ? (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10.5 6l3 4M13.5 6l-3 4"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            ) : (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10 5.5a3.5 3.5 0 010 5M12 3.5a6.5 6.5 0 010 9"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            )}
          </svg>
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(event) => setVolume(Number(event.target.value))}
          aria-label="Volume"
          className="h-1 w-20 accent-[var(--color-signal)]"
        />
      </div>

      <button
        type="button"
        onClick={stopListening}
        data-stop-listening
        className="shrink-0 rounded-[var(--radius-pill)] border border-line px-4 py-2 text-sm text-soft transition-colors hover:border-hot-edge hover:text-hot-text"
      >
        Stop listening
      </button>
    </div>
  );
}
